import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { 
  Receipt, 
  Download, 
  Building2, 
  CreditCard,
  Phone,
  User,
  MessageSquare,
  Banknote,
  RefreshCw,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bank templates
const bankTemplates = {
  ozon: {
    name: "Ozon Банк",
    fullName: "ООО «ОЗОН БАНК»",
    bik: "044525068",
    inn: "9703077050",
    corrAccount: "30101810645374525068",
    address: "Москва",
    phone1: "8 (800) 555-89-82",
    phone2: "",
    email: "",
    website: "ozon.ru",
    primaryColor: "#005BFF",
    useLocalPdf: true  // Flag for local PDF generation
  },
  alfabank: {
    name: "Альфа-Банк",
    fullName: "АО «АЛЬФА-БАНК»",
    bik: "044525593",
    inn: "7728168971",
    corrAccount: "30101810200000000593",
    address: "ул. Каланчёвская, 27, Москва, 107078",
    phone1: "+7 495 620 91 91",
    phone2: "+7 495 974 25 15",
    email: "mail@alfabank.ru",
    website: "alfabank.ru",
    primaryColor: "#EF3124"
  },
  sberbank: {
    name: "Сбербанк",
    fullName: "ПАО «Сбербанк»",
    bik: "044525225",
    inn: "7707083893",
    corrAccount: "30101810400000000225",
    address: "ул. Вавилова, 19, Москва, 117312",
    phone1: "+7 495 500 55 50",
    phone2: "900 (бесплатно)",
    email: "sberbank@sberbank.ru",
    website: "sberbank.ru",
    primaryColor: "#21A038"
  },
  tinkoff: {
    name: "Тинькофф",
    fullName: "АО «Тинькофф Банк»",
    bik: "044525974",
    inn: "7710140679",
    corrAccount: "30101810145250000974",
    address: "1-й Волоколамский пр-д, 10, стр. 1, Москва, 123060",
    phone1: "+7 800 555 77 78",
    phone2: "+7 495 648 11 11",
    email: "help@tinkoff.ru",
    website: "tinkoff.ru",
    primaryColor: "#FFDD2D"
  },
  vtb: {
    name: "ВТБ",
    fullName: "Банк ВТБ (ПАО)",
    bik: "044525187",
    inn: "7702070139",
    corrAccount: "30101810700000000187",
    address: "Дегтярный пер., 11, лит. А, Москва, 125009",
    phone1: "+7 800 100 24 24",
    phone2: "+7 495 777 24 24",
    email: "info@vtb.ru",
    website: "vtb.ru",
    primaryColor: "#0A2896",
    useVtbMobile: true  // Flag for VTB mobile app style
  },
  raiffeisen: {
    name: "Райффайзен",
    fullName: "АО «Райффайзенбанк»",
    bik: "044525700",
    inn: "7744000302",
    corrAccount: "30101810200000000700",
    address: "ул. Троицкая, 17, стр. 1, Москва, 129090",
    phone1: "+7 800 700 91 00",
    phone2: "+7 495 721 91 00",
    email: "info@raiffeisen.ru",
    website: "raiffeisen.ru",
    primaryColor: "#FFE600"
  },
  gazprombank: {
    name: "Газпромбанк",
    fullName: "Банк ГПБ (АО)",
    bik: "044525823",
    inn: "7744001497",
    corrAccount: "30101810200000000823",
    address: "ул. Наметкина, 16, корп. 1, Москва, 117420",
    phone1: "+7 800 100 07 01",
    phone2: "+7 495 913 74 74",
    email: "mailbox@gazprombank.ru",
    website: "gazprombank.ru",
    primaryColor: "#0066B3"
  },
  mynalog: {
    name: "Мой налог",
    fullName: "ФНС России",
    bik: "",
    inn: "",
    corrAccount: "",
    address: "",
    phone1: "",
    phone2: "",
    email: "",
    website: "lknpd.nalog.ru",
    primaryColor: "#1976D2",
    useMyNalog: true
  }
};

const recipientBanks = [
  "Озон Банк (Ozon)", "Сбербанк", "Тинькофф Банк", "Альфа-Банк", "ВТБ",
  "Райффайзенбанк", "Газпромбанк", "Почта Банк", "Росбанк", "Открытие"
];

export default function Receipts() {
  const [selectedBank, setSelectedBank] = useState("alfabank");
  const [formData, setFormData] = useState({
    amount: "10",
    commission: "0",
    recipientName: "Елена Алексеевна С",
    recipientPhone: "79216141664",
    recipientBank: "Озон Банк (Ozon)",
    senderAccount: "40817810080730003067",
    senderName: "",
    message: "Перевод денежных средств",
    operationNumber: "",
    sbpId: "",
    transferDate: "",
    formDate: ""
  });
  
  // My Nalog (Self-employed) receipt data
  const [myNalogData, setMyNalogData] = useState({
    checkNumber: "200pkylizq",
    checkDate: new Date().toLocaleString('ru-RU', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).replace(',', '') + "(+03:00)",
    fullName: "ПОТЕХИН ЮРИЙ ВЛАДИМИРОВИЧ",
    inn: "391802588200",
    serviceName: "Продажа фруктов",
    amount: "3001"
  });
  
  const [previewMode, setPreviewMode] = useState(false);
  
  // Ozon profile integration
  const [ozonProfiles, setOzonProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { 
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  // Fetch Ozon profiles when Ozon or VTB bank is selected
  useEffect(() => {
    if (selectedBank === 'ozon' || selectedBank === 'vtb') {
      fetchOzonProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBank]);

  const fetchOzonProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const response = await fetch(`${API_URL}/api/ozon/profiles`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setOzonProfiles(data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      toast.error("Ошибка загрузки профилей");
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleProfileSelect = (profileId) => {
    const profile = ozonProfiles.find(p => p.id === profileId);
    setSelectedProfile(profile);
    setSelectedTransaction(null);
  };

  const handleTransactionSelect = (txId) => {
    const tx = selectedProfile?.transactions?.find(t => t.id === txId);
    setSelectedTransaction(tx);
    
    if (tx) {
      // Auto-fill form from Ozon profile data
      setFormData(prev => ({
        ...prev,
        amount: tx.amount?.toString() || prev.amount,
        recipientPhone: selectedProfile?.phone || prev.recipientPhone,
        recipientName: selectedProfile?.name || prev.recipientName,
        senderName: tx.method || prev.senderName,
        transferDate: `${tx.date || ''} ${tx.time || ''}`.trim() || prev.transferDate
      }));
    }
  };

  // Generate Ozon PDF
  const handleGenerateOzonPdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/generate-local-check`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          bank: "Ozon Банк",
          amount: formData.amount,
          receiverPhone: formData.recipientPhone,
          receiverBank: "Ozon Банк",
          receiverName: formData.recipientName,
          senderName: formData.senderName,
          dateTime: formData.transferDate || generateFormDate(),
          sbpNumber: formData.sbpId || ""
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.pdf) {
        // Download PDF
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${data.pdf}`;
        link.download = data.filename || 'ozon_check.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("PDF скачан!");
      } else {
        toast.error(data.detail || "Ошибка генерации PDF");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Ошибка генерации PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Generate My Nalog (Self-employed) Check
  const handleGenerateMyNalogCheck = async () => {
    // Format amount with spaces and kopecks
    const formatAmount = (amount) => {
      const num = Number(amount || 0);
      // Format as "2 048,00" - space for thousands, comma for decimals
      const formatted = num.toFixed(2).replace('.', ',');
      const parts = formatted.split(',');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    };
    
    // Generate QR code URL based on check number
    const qrUrl = `https://lknpd.nalog.ru/api/v1/receipt/${myNalogData.checkNumber}/print`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}`;
    
    // Split full name into parts
    const nameParts = myNalogData.fullName.split(' ');
    const lastName = nameParts[0] || '';
    const firstName = nameParts.slice(1).join(' ') || '';
    
    // Create hidden container for rendering
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);
    
    container.innerHTML = `
      <div id="mynalog-receipt" style="
        width: 450px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 12px;
        padding: 20px;
        font-family: Arial, sans-serif;
      ">
        <div style="display: flex; align-items: center; padding-bottom: 15px; border-bottom: 1px solid #e0e0e0;">
          <div style="padding-right: 20px; border-right: 1px solid #e0e0e0; display: flex; align-items: center; align-self: stretch;">
            <span style="font-size: 32px; font-weight: bold; color: #333;">ЧЕК</span>
          </div>
          <div style="padding-left: 20px; flex: 1;">
            <div style="font-size: 18px; font-weight: bold; color: #333; margin-bottom: 2px;">Чек № ${myNalogData.checkNumber}</div>
            <div style="font-size: 15px; font-weight: bold; color: #333;">${myNalogData.checkDate}</div>
            <div style="font-size: 15px; font-weight: bold; color: #333;">Режим НО: НПД</div>
          </div>
        </div>
        
        <div style="padding: 18px 0;">
          <div style="font-size: 20px; font-weight: bold; color: #333; margin-bottom: 3px;">${lastName}</div>
          <div style="font-size: 20px; font-weight: bold; color: #333;">${firstName}</div>
        </div>
        
        <div style="display: flex; justify-content: space-between; padding: 12px 0 18px 0; border-top: 1px solid #e0e0e0; font-size: 14px; font-weight: bold; color: #333;">
          <span>Наименование услуг</span>
          <span>Сумма</span>
        </div>
        
        <div style="display: flex; align-items: center; padding: 12px 0 20px 0; gap: 12px;">
          <span style="font-size: 14px; font-weight: bold; color: #333;">1</span>
          <span style="flex: 1; font-size: 16px; color: #333; line-height: 1.2;">${myNalogData.serviceName}</span>
          <span style="font-size: 16px; font-weight: bold; color: #333; white-space: nowrap; line-height: 1.2;">${formatAmount(myNalogData.amount)} ₽</span>
        </div>
        
        <div style="border-top: 2px dashed #bbb; margin-bottom: 15px;"></div>
        
        <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 0;">
          <span style="font-size: 18px; font-weight: bold; color: #333;">Итого</span>
          <span style="font-size: 20px; font-weight: bold; color: #333;">${formatAmount(myNalogData.amount)} ₽</span>
        </div>
        
        <div style="display: flex; justify-content: space-between; padding: 10px 0; font-size: 14px; font-weight: bold; border-top: 1px solid #e0e0e0;">
          <span style="color: #333;">ИНН</span>
          <span style="color: #333;">${myNalogData.inn}</span>
        </div>
        
        <div style="padding-top: 20px; display: flex; justify-content: center;">
          <img src="${qrCodeUrl}" alt="QR Code" style="width: 130px; height: 130px;" crossorigin="anonymous" />
        </div>
      </div>
    `;
    
    // Wait for QR code to load
    const qrImg = container.querySelector('img');
    await new Promise((resolve) => {
      qrImg.onload = resolve;
      qrImg.onerror = resolve;
      setTimeout(resolve, 2000); // fallback timeout
    });
    
    try {
      const receiptElement = container.querySelector('#mynalog-receipt');
      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });
      
      // Download as PNG
      const link = document.createElement('a');
      link.download = `check_${myNalogData.checkNumber}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("Чек скачан!");
    } catch (error) {
      console.error('Error generating check:', error);
      toast.error("Ошибка генерации чека");
    } finally {
      document.body.removeChild(container);
    }
  };

  // Generate VTB SBP Check as image - EXACT REPLICA
  const handleGenerateVtbCheck = () => {
    const printWindow = window.open('', '_blank');
    
    // Format phone number
    const formatPhone = (phone) => {
      const digits = phone?.replace(/\D/g, '') || '';
      if (digits.length === 11 && digits.startsWith('7')) {
        return `+7 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
      }
      if (digits.length === 10) {
        return `+7 (${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,8)}-${digits.slice(8,10)}`;
      }
      return phone || '+7 (XXX) XXX-XX-XX';
    };

    // Format amount with spaces (100000 -> 100 000)
    const formatAmount = (amount) => {
      return Number(amount || 0).toLocaleString('ru-RU').replace(/,/g, ' ');
    };

    // Generate SBP ID like B515634889313224000017001 1510301
    const generateVtbSbpId = () => {
      let id = 'B';
      for (let i = 0; i < 24; i++) {
        id += Math.floor(Math.random() * 10);
      }
      const part2 = Math.floor(1000000 + Math.random() * 9000000);
      return { part1: id, part2: part2.toString() };
    };

    // Get date and time
    const dateStr = formData.transferDate?.split(' ')[0] || new Date().toLocaleDateString('ru-RU');
    const timeStr = formData.transferDate?.split(' ')[1]?.substring(0, 5) || new Date().toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
    const sbpId = generateVtbSbpId();
    
    // Shorten name: "Иван Иванович Иванов" -> "Иван Иванович И."
    const shortenName = (name) => {
      if (!name) return 'Получатель';
      const parts = name.trim().split(' ');
      if (parts.length >= 3) {
        return `${parts[0]} ${parts[1]} ${parts[2].charAt(0)}.`;
      }
      if (parts.length === 2) {
        return `${parts[0]} ${parts[1].charAt(0)}.`;
      }
      return name;
    };

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>ВТБ Чек СБП</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Inter', sans-serif;
      background: #fff;
      padding: 40px;
      font-size: 24px;
      -webkit-font-smoothing: antialiased;
      color: #000;
    }
    .receipt {
      width: 580px;
      margin: 0 auto;
    }
    .logo-center {
      text-align: center;
      margin-bottom: 20px;
    }
    .logo-center img {
      height: 48px;
      width: auto;
    }
    .blue-line {
      height: 3px;
      background: #0066CC;
      margin-bottom: 35px;
    }
    .header-center {
      text-align: center;
      margin-bottom: 35px;
    }
    .transfer-type {
      font-size: 24px;
      color: #0066CC;
      font-weight: 400;
      margin-bottom: 5px;
    }
    .recipient-name {
      font-size: 26px;
      font-weight: 500;
      color: #000;
    }
    .row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 14px 0;
      font-size: 24px;
    }
    .label {
      color: #000;
      font-weight: 400;
    }
    .value {
      color: #000;
      text-align: right;
      font-weight: 400;
    }
    .value.green {
      color: #00A651;
      font-weight: 500;
    }
    .value.sbp {
      font-size: 20px;
      line-height: 1.5;
      text-align: right;
    }
    .amount-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 25px 0;
      margin-top: 15px;
    }
    .amount-label {
      font-size: 26px;
      font-weight: 500;
      color: #000;
    }
    .amount-value {
      font-size: 38px;
      font-weight: 500;
      color: #000;
    }
    .footer-btn {
      margin-top: 30px;
      padding: 18px 35px;
      border: 2px solid #0066CC;
      border-radius: 10px;
      text-align: center;
      display: inline-block;
    }
    .footer-btn-text {
      font-size: 20px;
      color: #0066CC;
      font-weight: 400;
      line-height: 1.4;
    }
    @media print {
      body { padding: 30px; }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="logo-center">
      <img src="https://customer-assets.emergentagent.com/job_b023e09f-5d59-4d21-bf4c-c551b773c359/artifacts/0fe8dpok_image.png" alt="ВТБ" style="height: 60px; width: auto;" />
    </div>
    <div class="blue-line"></div>
    <div class="header-center">
      <div class="transfer-type">Исходящий перевод СБП</div>
      <div class="recipient-name">\${shortenName(formData.recipientName)}</div>
    </div>
    <div class="row">
      <span class="label">Статус</span>
      <span class="value green">Выполнено</span>
    </div>
    <div class="row">
      <span class="label">Дата операции</span>
      <span class="value">\${dateStr}, \${timeStr}</span>
    </div>
    <div class="row">
      <span class="label">Счет списания</span>
      <span class="value">*${formData.senderAccount?.replace(/\\D/g, '').slice(-4) || '1234'}</span>
    </div>
    <div class="row">
      <span class="label">Имя плательщика</span>
      <span class="value">${formData.senderName || 'Иван Иванович И.'}</span>
    </div>
    <div class="row">
      <span class="label">Получатель</span>
      <span class="value">${shortenName(formData.recipientName)}</span>
    </div>
    <div class="row">
      <span class="label">Телефон получателя</span>
      <span class="value">${formatPhone(formData.recipientPhone)}</span>
    </div>
    <div class="row">
      <span class="label">Банк получателя</span>
      <span class="value">${formData.recipientBank || 'Сбербанк'}</span>
    </div>
    <div class="row">
      <span class="label">ID операции в СБП</span>
      <span class="value sbp">${sbpId.part1}<br>${sbpId.part2}</span>
    </div>
    <div class="amount-row">
      <span class="amount-label">Сумма операции</span>
      <span class="amount-value">${formatAmount(formData.amount)} Р</span>
    </div>
    <div class="footer-btn">
      <div class="footer-btn-text">Банк ВТБ (ПАО)<br>Операция выполнена</div>
    </div>
  </div>
</body>
</html>`);
    printWindow.document.close();
  };

  const generateOperationNumber = () => {
    const prefix = "C";
    const date = new Date();
    const datePart = `${date.getDate().toString().padStart(2, '0')}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getFullYear().toString().slice(-2)}`;
    const random = Math.floor(Math.random() * 9000000) + 1000000;
    return `${prefix}${datePart}${random}`;
  };

  const generateSbpId = () => {
    const chars = "0123456789ABCDEF";
    let result = "A";
    for (let i = 0; i < 31; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const generateDateTime = () => {
    const now = new Date();
    return `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
  };

  const generateFormDate = () => {
    const now = new Date();
    return `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  };

  const handleGenerateReceipt = () => {
    setFormData(prev => ({
      ...prev,
      operationNumber: prev.operationNumber || generateOperationNumber(),
      sbpId: prev.sbpId || generateSbpId(),
      transferDate: prev.transferDate || generateDateTime(),
      formDate: prev.formDate || generateFormDate()
    }));
    setPreviewMode(true);
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    const bank = bankTemplates[selectedBank];
    
    if (selectedBank === 'alfabank') {
      // ALFA-BANK EXACT PDF TEMPLATE
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title></title>
  <style>
    @page { 
      size: A4; 
      margin: 0;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    * { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
    html, body { height: 100%; margin: 0; padding: 0; }
    body { color: #000; line-height: 1.3; }
    .page { 
      width: 210mm; 
      height: 297mm; 
      padding: 10mm;
      position: relative;
      box-sizing: border-box;
    }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
    .logo { width: 50px; height: auto; }
    .form-date { text-align: right; font-size: 15px; color: #888; }
    .title { font-size: 27px; font-weight: normal; color: #000; margin-bottom: 20px; }
    .content { display: flex; gap: 40px; margin-bottom: 20px; }
    .column { flex: 1; }
    .field { margin-bottom: 15px; }
    .field-label { font-size: 15px; color: #888; margin-bottom: 3px; }
    .field-value { font-size: 16px; color: #000; }
    .stamp { }
    .stamp img { width: 250px; height: auto; }
    .footer { 
      position: absolute; 
      bottom: 10mm; 
      right: 10mm; 
    }
    .footer img { width: 450px; height: auto; }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/axcumlam_image.png" alt="Альфа-Банк" class="logo" />
      <div class="form-date">
        <div>Сформирована</div>
        <div>${formData.formDate} мск</div>
      </div>
    </div>
    <div class="title">Квитанция о переводе по СБП</div>
    <div class="content">
      <div class="column">
        <div class="field"><div class="field-label">Сумма перевода</div><div class="field-value">${formData.amount} RUR</div></div>
        <div class="field"><div class="field-label">Комиссия</div><div class="field-value">${formData.commission} RUR</div></div>
        <div class="field"><div class="field-label">Дата и время перевода</div><div class="field-value">${formData.transferDate} мск</div></div>
        <div class="field"><div class="field-label">Номер операции</div><div class="field-value">${formData.operationNumber}</div></div>
        <div class="field"><div class="field-label">Получатель</div><div class="field-value">${formData.recipientName}</div></div>
      </div>
      <div class="column">
        <div class="field"><div class="field-label">Номер телефона получателя</div><div class="field-value">${formData.recipientPhone}</div></div>
        <div class="field"><div class="field-label">Банк получателя</div><div class="field-value">${formData.recipientBank}</div></div>
        <div class="field"><div class="field-label">Счёт списания</div><div class="field-value">${formData.senderAccount}</div></div>
        <div class="field"><div class="field-label">Идентификатор операции в СБП</div><div class="field-value">${formData.sbpId}</div></div>
        <div class="field"><div class="field-label">Сообщение получателю</div><div class="field-value">${formData.message}</div></div>
      </div>
    </div>
    <div class="stamp">
      <img src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/xf63iasw_image.png" alt="Печать" />
    </div>
    <div class="footer">
      <img src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/9sgazdn7_image.png" alt="Контакты" />
    </div>
  </div>
  <script>window.print();</script>
</body>
</html>`);
    } else {
      // OTHER BANKS PDF TEMPLATE
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Квитанция СБП</title>
  <style>
    @page { size: A4; margin: 20mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
    html, body { height: 100%; }
    body { color: #333; line-height: 1.4; position: relative; min-height: 100vh; }
    .page-container { position: relative; min-height: calc(100vh - 40mm); padding-bottom: 120px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; }
    .logo { width: 50px; height: 50px; background: ${bank.primaryColor}; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
    .logo span { color: white; font-size: 32px; font-weight: bold; }
    .form-date { text-align: right; font-size: 12px; color: #666; }
    .title { font-size: 28px; font-weight: bold; color: #333; margin-bottom: 30px; }
    .content { display: flex; gap: 60px; margin-bottom: 40px; }
    .column { flex: 1; }
    .field { margin-bottom: 20px; }
    .field-label { font-size: 14px; color: #555; margin-bottom: 4px; }
    .field-value { font-size: 16px; color: #000; }
    .field-value.small { font-size: 12px; word-break: break-all; }
    .bank-box { border: 2px solid #2196F3; border-radius: 6px; padding: 20px 25px; display: inline-block; text-align: center; width: 340px; }
    .bank-name { font-size: 14px; color: #2196F3; margin-bottom: 8px; }
    .bank-details { font-size: 12px; color: #2196F3; margin-bottom: 4px; }
    .status { font-size: 16px; font-weight: bold; color: #2196F3; margin-top: 12px; }
    .footer { display: flex; justify-content: space-between; font-size: 12px; color: #666; padding-top: 25px; border-top: 1px solid #eee; margin-top: 30px; }
    .footer-left .website { font-weight: bold; color: #333; margin-bottom: 5px; }
    .footer-right { text-align: right; }
  </style>
</head>
<body>
  <div class="page-container">
  <div class="header">
    <div class="logo"><span>${bank.name.charAt(0)}</span></div>
    <div class="form-date">Сформирована<br>${formData.formDate} мск</div>
  </div>
  <div class="title">Квитанция о переводе по СБП</div>
  <div class="content">
    <div class="column">
      <div class="field"><div class="field-label">Сумма перевода</div><div class="field-value">${formData.amount} RUR</div></div>
      <div class="field"><div class="field-label">Комиссия</div><div class="field-value">${formData.commission} RUR</div></div>
      <div class="field"><div class="field-label">Дата и время перевода</div><div class="field-value">${formData.transferDate} мск</div></div>
      <div class="field"><div class="field-label">Номер операции</div><div class="field-value">${formData.operationNumber}</div></div>
      <div class="field"><div class="field-label">Получатель</div><div class="field-value">${formData.recipientName}</div></div>
    </div>
    <div class="column">
      <div class="field"><div class="field-label">Номер телефона получателя</div><div class="field-value">${formData.recipientPhone}</div></div>
      <div class="field"><div class="field-label">Банк получателя</div><div class="field-value">${formData.recipientBank}</div></div>
      <div class="field"><div class="field-label">Счёт списания</div><div class="field-value">${formData.senderAccount}</div></div>
      <div class="field"><div class="field-label">Идентификатор операции в СБП</div><div class="field-value small">${formData.sbpId}</div></div>
      <div class="field"><div class="field-label">Сообщение получателю</div><div class="field-value">${formData.message}</div></div>
    </div>
  </div>
  <div class="bank-box">
    <div class="bank-name">${bank.fullName}</div>
    <div class="bank-details">БИК ${bank.bik} ИНН ${bank.inn}</div>
    <div class="bank-details">к/сч ${bank.corrAccount}</div>
    <div class="status">ПЕРЕВОД ВЫПОЛНЕН</div>
  </div>
  <div class="footer">
    <div class="footer-left">
      <div class="website">${bank.website}</div>
      <div>${bank.fullName}</div>
      <div>${bank.address}</div>
    </div>
    <div class="footer-right">
      <div>${bank.phone1}</div>
      <div>${bank.phone2}</div>
      <div>${bank.email}</div>
    </div>
  </div>
  </div>
  <script>window.print();</script>
</body>
</html>`);
    }
    printWindow.document.close();
    toast.success("Откройте диалог печати → Сохранить как PDF");
  };

  const bank = bankTemplates[selectedBank];

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
            <Receipt className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Генератор чеков СБП</h1>
            <p className="text-sm text-slate-500">Создание квитанций о переводах</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Банк отправителя
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {Object.entries(bankTemplates).map(([key, bankInfo]) => (
              <button
                key={key}
                onClick={() => {
                  setSelectedBank(key);
                  if (key !== 'ozon' && key !== 'vtb') {
                    setSelectedProfile(null);
                    setSelectedTransaction(null);
                  }
                }}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedBank === key ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="w-8 h-8 rounded flex items-center justify-center text-white font-bold mb-2 mx-auto"
                  style={{ backgroundColor: bankInfo.primaryColor }}>
                  {bankInfo.name.charAt(0)}
                </div>
                <p className="text-xs font-medium text-center">{bankInfo.name}</p>
              </button>
            ))}
          </div>

          {/* VTB Profile Selection */}
          {selectedBank === 'vtb' && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Данные из OZON профиля для чека ВТБ
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">OZON Профиль</label>
                  <Select onValueChange={handleProfileSelect} value={selectedProfile?.id || ""}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder={loadingProfiles ? "Загрузка..." : "Выберите профиль"} />
                    </SelectTrigger>
                    <SelectContent>
                      {ozonProfiles.map(profile => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name} ({profile.phone})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Транзакция</label>
                  <Select 
                    onValueChange={handleTransactionSelect} 
                    value={selectedTransaction?.id || ""}
                    disabled={!selectedProfile}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Выберите транзакцию" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProfile?.transactions?.map(tx => (
                        <SelectItem key={tx.id} value={tx.id}>
                          {tx.amount} ₽ - {tx.date} {tx.time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={fetchOzonProfiles} 
                  variant="outline" 
                  size="sm"
                  disabled={loadingProfiles}
                >
                  {loadingProfiles ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Обновить профили
                </Button>
                <Button 
                  onClick={handleGenerateVtbCheck} 
                  size="sm"
                  disabled={!selectedProfile}
                  className="bg-[#0A2896] hover:bg-[#081d6b]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Сгенерировать чек ВТБ
                </Button>
              </div>
            </div>
          )}

          {/* My Nalog Form */}
          {selectedBank === 'mynalog' && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-4 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Чек самозанятого (Мой налог)
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Номер чека</label>
                    <Input 
                      value={myNalogData.checkNumber} 
                      onChange={(e) => setMyNalogData({...myNalogData, checkNumber: e.target.value})}
                      placeholder="200pkylizq"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Дата и время</label>
                    <Input 
                      value={myNalogData.checkDate} 
                      onChange={(e) => setMyNalogData({...myNalogData, checkDate: e.target.value})}
                      placeholder="18.01.2026 23:59:59(+03:00)"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ФИО (полностью)</label>
                  <Input 
                    value={myNalogData.fullName} 
                    onChange={(e) => setMyNalogData({...myNalogData, fullName: e.target.value})}
                    placeholder="ПОТЕХИН ЮРИЙ ВЛАДИМИРОВИЧ"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ИНН</label>
                  <Input 
                    value={myNalogData.inn} 
                    onChange={(e) => setMyNalogData({...myNalogData, inn: e.target.value})}
                    placeholder="391802588200"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Наименование услуги/товара</label>
                  <Input 
                    value={myNalogData.serviceName} 
                    onChange={(e) => setMyNalogData({...myNalogData, serviceName: e.target.value})}
                    placeholder="Продажа фруктов"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Сумма (₽)</label>
                  <Input 
                    type="number"
                    value={myNalogData.amount} 
                    onChange={(e) => setMyNalogData({...myNalogData, amount: e.target.value})}
                    placeholder="3001"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={handleGenerateMyNalogCheck}
                  className="bg-[#1976D2] hover:bg-[#1565C0]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Сгенерировать чек
                </Button>
              </div>
            </div>
          )}

          {/* Ozon Profile Selection */}
          {selectedBank === 'ozon' && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Данные из OZON профиля
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">OZON Профиль</label>
                  <Select onValueChange={handleProfileSelect} value={selectedProfile?.id || ""}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder={loadingProfiles ? "Загрузка..." : "Выберите профиль"} />
                    </SelectTrigger>
                    <SelectContent>
                      {ozonProfiles.map(profile => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name} ({profile.phone})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Транзакция</label>
                  <Select 
                    onValueChange={handleTransactionSelect} 
                    value={selectedTransaction?.id || ""}
                    disabled={!selectedProfile}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Выберите транзакцию" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProfile?.transactions?.map(tx => (
                        <SelectItem key={tx.id} value={tx.id}>
                          {tx.amount} ₽ - {tx.date} {tx.time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={fetchOzonProfiles} 
                variant="outline" 
                size="sm"
                disabled={loadingProfiles}
              >
                {loadingProfiles ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Обновить профили
              </Button>
            </div>
          )}

          {/* Standard SBP form - hide for mynalog */}
          {selectedBank !== 'mynalog' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1"><Banknote className="w-4 h-4 inline mr-1" />Сумма</label>
                <Input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Комиссия</label>
                <Input type="number" value={formData.commission} onChange={(e) => setFormData({ ...formData, commission: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><User className="w-4 h-4 inline mr-1" />ФИО получателя</label>
              <Input value={formData.recipientName} onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1"><Phone className="w-4 h-4 inline mr-1" />Телефон</label>
                <Input value={formData.recipientPhone} onChange={(e) => setFormData({ ...formData, recipientPhone: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1"><Building2 className="w-4 h-4 inline mr-1" />Банк</label>
                <select value={formData.recipientBank} onChange={(e) => setFormData({ ...formData, recipientBank: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  {recipientBanks.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><CreditCard className="w-4 h-4 inline mr-1" />Счёт списания</label>
              <Input value={formData.senderAccount} onChange={(e) => setFormData({ ...formData, senderAccount: e.target.value })} />
            </div>
            
            {/* Sender name for Ozon */}
            {selectedBank === 'ozon' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1"><User className="w-4 h-4 inline mr-1" />ФИО отправителя</label>
                <Input value={formData.senderName} onChange={(e) => setFormData({ ...formData, senderName: e.target.value })} placeholder="Имя отправителя" />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1"><MessageSquare className="w-4 h-4 inline mr-1" />Сообщение</label>
              <Input value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
            </div>
            
            {/* Different buttons for Ozon vs other banks */}
            {selectedBank === 'ozon' ? (
              <Button 
                onClick={handleGenerateOzonPdf} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={generatingPdf}
              >
                {generatingPdf ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Генерация...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Скачать PDF (Ozon)</>
                )}
              </Button>
            ) : (
              <Button onClick={handleGenerateReceipt} className="w-full bg-green-600 hover:bg-green-700">
                <Receipt className="w-4 h-4 mr-2" />Сформировать чек
              </Button>
            )}
          </div>
          )}
        </div>

        {/* Preview - EXACT Original Style */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Предпросмотр</h2>
            {previewMode && (
              <Button onClick={handleDownloadPDF} size="sm">
                <Download className="w-4 h-4 mr-2" />PDF
              </Button>
            )}
          </div>
          
          {previewMode ? (
            selectedBank === 'alfabank' ? (
              /* ALFA-BANK EXACT TEMPLATE */
              <div className="bg-white" style={{ 
                fontFamily: 'Arial, sans-serif', 
                padding: '10mm',
                minHeight: '1100px', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative'
              }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                  {/* Alfa-Bank Logo - exact image */}
                  <img 
                    src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/axcumlam_image.png" 
                    alt="Альфа-Банк"
                    style={{ width: '50px', height: 'auto' }}
                  />
                  {/* Form date - gray color */}
                  <div style={{ textAlign: 'right', fontSize: '15px', color: '#888' }}>
                    <div>Сформирована</div>
                    <div>{formData.formDate} мск</div>
                  </div>
                </div>
                
                {/* Title - NOT bold */}
                <h3 style={{ 
                  fontSize: '27px', 
                  fontWeight: 'normal', 
                  marginBottom: '25px', 
                  color: '#000' 
                }}>
                  Квитанция о переводе по СБП
                </h3>
                
                {/* Two Columns Content */}
                <div style={{ display: 'flex', gap: '40px', marginBottom: '25px' }}>
                  {/* Left Column */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Сумма перевода</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.amount} RUR</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Комиссия</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.commission} RUR</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Дата и время перевода</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.transferDate} мск</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Номер операции</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.operationNumber}</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Получатель</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientName}</div>
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Номер телефона получателя</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientPhone}</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Банк получателя</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientBank}</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Счёт списания</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.senderAccount}</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Идентификатор операции в СБП</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.sbpId}</div>
                    </div>
                    <div style={{ marginBottom: '18px' }}>
                      <div style={{ fontSize: '15px', color: '#888', marginBottom: '3px' }}>Сообщение получателю</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.message}</div>
                    </div>
                  </div>
                </div>
                
                {/* Stamp - below text, left aligned */}
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/xf63iasw_image.png" 
                    alt="Печать Альфа-Банк"
                    style={{ width: '280px', height: 'auto' }}
                  />
                </div>
                
                {/* Spacer to push footer to absolute bottom */}
                <div style={{ flex: 1 }}></div>
                
                {/* Footer - exact image in bottom right */}
                <div style={{ 
                  position: 'absolute',
                  bottom: '10mm',
                  right: '10mm'
                }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_crm-suite-20/artifacts/9sgazdn7_image.png" 
                    alt="Альфа-Банк контакты"
                    style={{ width: '500px', height: 'auto' }}
                  />
                </div>
              </div>
            ) : (
              /* OTHER BANKS TEMPLATE */
              <div className="p-6 bg-white" style={{ fontFamily: 'Arial, sans-serif', position: 'relative', minHeight: '700px' }}>
                {/* Header with Logo */}
                <div className="flex justify-between items-start mb-6">
                  <div 
                    className="flex items-center justify-center rounded-md"
                    style={{ width: '50px', height: '50px', backgroundColor: bank.primaryColor }}
                  >
                    <span className="text-white font-bold" style={{ fontSize: '32px' }}>{bank.name.charAt(0)}</span>
                  </div>
                  <div className="text-right" style={{ fontSize: '12px', color: '#666' }}>
                    <div>Сформирована</div>
                    <div>{formData.formDate} мск</div>
                  </div>
                </div>
                
                {/* Title */}
                <h3 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '30px', color: '#333' }}>
                  Квитанция о переводе по СБП
                </h3>
                
                {/* Two Columns */}
                <div className="flex gap-12 mb-10">
                  <div className="flex-1 space-y-5">
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Сумма перевода</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.amount} RUR</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Комиссия</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.commission} RUR</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Дата и время перевода</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.transferDate} мск</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Номер операции</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.operationNumber}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Получатель</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientName}</div>
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-5">
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Номер телефона получателя</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientPhone}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Банк получателя</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.recipientBank}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Счёт списания</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.senderAccount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Идентификатор операции в СБП</div>
                      <div style={{ fontSize: '12px', color: '#000', wordBreak: 'break-all' }}>{formData.sbpId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', color: '#555', marginBottom: '4px' }}>Сообщение получателю</div>
                      <div style={{ fontSize: '16px', color: '#000' }}>{formData.message}</div>
                    </div>
                  </div>
                </div>
                
                {/* Bank Box */}
                <div 
                  className="inline-block mb-8"
                  style={{ 
                    border: '2px solid #2196F3',
                    borderRadius: '6px',
                    padding: '20px 25px',
                    textAlign: 'center',
                    width: '340px'
                  }}
                >
                  <div style={{ color: '#2196F3', fontSize: '14px', marginBottom: '8px' }}>{bank.fullName}</div>
                  <div style={{ color: '#2196F3', fontSize: '12px', marginBottom: '4px' }}>БИК {bank.bik} ИНН {bank.inn}</div>
                  <div style={{ color: '#2196F3', fontSize: '12px', marginBottom: '12px' }}>к/сч {bank.corrAccount}</div>
                  <div style={{ color: '#2196F3', fontSize: '16px', fontWeight: 'bold' }}>ПЕРЕВОД ВЫПОЛНЕН</div>
                </div>
                
                {/* Footer */}
                <div className="flex justify-between pt-6 border-t border-gray-200" style={{ fontSize: '12px', color: '#666' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>{bank.website}</div>
                    <div>{bank.fullName}</div>
                    <div>{bank.address}</div>
                  </div>
                  <div className="text-right">
                    <div>{bank.phone1}</div>
                    <div>{bank.phone2}</div>
                    <div>{bank.email}</div>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="p-8 text-center">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Заполните форму и нажмите &quot;Сформировать чек&quot;</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
